import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { StoreBadges } from '../components/StoreCTA';
import { APPS } from '../utils/storeLinks';
import '../styles/sitePages.css';
import './Apps.css';
import { Helmet } from 'react-helmet-async';

// Stats refreshed 2026-10-12 (nucleus data). Downloads = App Store first-time
// downloads (App Store Connect) + Google Play lifetime installs (Play listing),
// rounded down. Rating = the higher of the US App Store rating and the Google
// Play rating, shown only once a store has 10+ ratings. Apps under 100
// downloads show "New" instead of a count.
const apps = [
  {
    id: 'daily-logic-puzzles',
    link: '/apps/daily-logic-puzzles',
    storeKey: 'dlp',
    title: 'Daily Logic Puzzles',
    description: 'One app, eight daily logic puzzles. Fresh challenges every day including Crowns, Binary Sudoku, Mine Finder, Sudoku, and more. Minimal ads, just satisfying solves.',
    imageUrl: '/assets/logos/dailylogicpuzzles_logo.webp',
    rating: '4.7',
    downloads: '10K+'
  },
  {
    id: 'crowns',
    link: '/apps/crowns',
    storeKey: 'crowns',
    title: 'Crowns Logic Game',
    description: 'The ultimate puzzle game where each row, column, and color region must contain exactly one crown, and crowns cannot be placed in adjacent cells. Features over 1,500 puzzles!',
    imageUrl: '/assets/logos/crowns_logo_256.webp',
    rating: '4.8',
    downloads: '155K+'
  },
  {
    id: 'binarysudokuplus',
    link: '/apps/binarysudokuplus',
    storeKey: 'bsp',
    title: 'Binary Sudoku+',
    description: 'Create perfect symmetry by placing tiles strategically on the board in this challenging puzzle game. Test your spatial reasoning across 1,300+ levels!',
    imageUrl: '/assets/logos/symmetrix_logo.webp',
    rating: '4.7',
    downloads: '25K+'
  },
  {
    id: 'aquarium',
    link: '/apps/aquarium',
    storeKey: 'aquarium',
    title: 'Aquarium Logic Puzzle',
    description: 'A captivating logic puzzle game where you strategically fill aquariums to specific water levels, guided by numerical clues. Beautiful animations and challenging puzzles!',
    imageUrl: '/assets/logos/aqua_logo.webp',
    rating: '4.6',
    downloads: '4K+'
  },
  {
    id: 'palettepath',
    link: '/apps/palettepath',
    storeKey: 'palettepath',
    title: 'Color Flood: Logic Puzzle',
    description: 'A colorful puzzle game where you connect matching colors while filling the entire board. Create beautiful patterns while exercising your logical thinking!',
    imageUrl: '/assets/logos/palettepath_logo.png',
    rating: null,
    downloads: '6K+'
  },
  {
    id: 'dotstream',
    link: '/apps/dotstream',
    storeKey: 'dotstream',
    title: 'Dot Stream',
    description: 'Connect dots to create patterns and solve puzzles in this elegant logic game. Draw paths through numbered dots to fill the entire grid!',
    imageUrl: '/assets/logos/dotstream_logo.webp',
    rating: '4.7',
    downloads: '3K+'
  },
  {
    id: 'fleet',
    storeKey: 'fleet',
    title: 'Fleet Logic Puzzle',
    description: 'Find every hidden ship using row and column clues. Ships never touch, not even diagonally, and every puzzle has exactly one solution. 500 levels in three sizes.',
    imageUrl: '/assets/logos/fleet_app_256.webp',
    rating: null,
    downloads: null,
    link: '/play/fleet'
  },
  {
    id: 'minefinder',
    storeKey: 'minefinder',
    title: 'Mine Finder: No Guess Logic',
    description: 'The classic mine puzzle with no 50/50 coin flips. Every board is checked by a logic solver, the first tap is always safe, and hints explain the move you can prove.',
    imageUrl: '/assets/logos/minefinder_app_256.webp',
    rating: null,
    downloads: null,
    link: '/play/minefinder'
  },
  {
    id: 'nonogram',
    storeKey: 'nonogram',
    title: 'Nonogram Puzzles: Pixel Logic',
    description: 'Use the number clues to fill the grid and reveal a hidden pixel picture, which blooms into full color when you finish. One answer per puzzle, no guessing.',
    imageUrl: '/assets/logos/nonogram_app_256.webp',
    rating: null,
    downloads: null,
    link: '/play/nonograms'
  },
  {
    id: 'calcudoku',
    storeKey: 'calcudoku',
    title: 'Calcudoku: Math Logic Puzzle',
    description: 'Fill the grid so no number repeats in a row or column, and make every cage hit its target with add, subtract, multiply or divide. 560 levels plus a daily puzzle.',
    imageUrl: '/assets/logos/calcudoku_app_256.webp',
    rating: null,
    downloads: null,
    link: '/play/calcudoku'
  },
  {
    id: 'domino',
    storeKey: 'domino',
    title: 'Domino Puzzle: Logic Game',
    description: 'Place a handful of domino tiles on a shaped board so every colored region keeps its rule: a sum, all equal, all different, under or over. Always solvable by logic.',
    imageUrl: '/assets/logos/domino_app_256.webp',
    rating: null,
    downloads: null
  },
  {
    id: 'logicgrid',
    storeKey: 'logicgrid',
    title: 'Logic Grid: Deduction Puzzles',
    description: 'Read the clues, cross off what can\'t be true, and crack the case. 400 story puzzles from three-list warm-ups to big five-list grids, plus a daily case.',
    imageUrl: '/assets/logos/logic_grid_app_256.webp',
    rating: null,
    downloads: null,
    link: '/play/logicgridpuzzles'
  },
  {
    id: 'buzzbloom',
    storeKey: 'buzzbloom',
    title: 'Buzzbloom: Bee Logic Puzzle',
    description: 'Place one bee in every row, column and flower bed, with no two bees touching, not even diagonally. A calm daily brain break that grows your own garden.',
    imageUrl: '/assets/logos/buzzbloom_app_256.webp',
    rating: null,
    downloads: null
  }
];

function AppCardBody({ app }) {
  return (
    <>
      <div className="app-card-top">
        <img className="app-card-icon" src={app.imageUrl} alt={`${app.title} mobile app icon`} width="72" height="72" loading="lazy" />
        <div>
          <h2>{app.title}</h2>
          <p className="app-stats">
            {app.rating && <span className="app-stat-rating"><span aria-hidden="true">★</span> {app.rating}</span>}
            {app.downloads
              ? <span className="app-stat-downloads">{app.downloads} Downloads</span>
              : <span className="app-stat-new">New</span>}
            {APPS[app.storeKey].androidOnly && <span className="app-stat-platform">Android</span>}
          </p>
        </div>
      </div>
      <p className="app-description">{app.description}</p>
    </>
  );
}

function Apps() {
  return (
    <div>
      <Helmet>
        <title>Mobile Logic Puzzle Apps - Download for iOS & Android</title>
        <meta name="description" content="Download our 13 logic puzzle apps for iOS and Android, including Daily Logic Puzzles, Crowns, Binary Sudoku+, Calcudoku and Mine Finder. Over 200K downloads, and Crowns is rated 4.8 stars on the App Store." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps" />
        <meta property="og:title" content="Mobile Logic Puzzle Apps - Download for iOS & Android" />
        <meta property="og:description" content="Download our logic puzzle apps! Over 200K downloads, and Crowns is rated 4.8 stars on the App Store." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mobile Logic Puzzle Apps - Download for iOS & Android" />
        <meta name="twitter:description" content="Download our logic puzzle apps! Over 200K downloads, and Crowns is rated 4.8 stars on the App Store." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>
      <NavBar />
      <div className="apps-page">
        <header className="sp-hero">
          <div className="container">
            <span className="eyebrow">iPhone, iPad &amp; Android</span>
            <h1>Mobile Apps</h1>
            <p className="sp-hero__lead">
              Take our challenging puzzle games with you wherever you go! Download our mobile apps from the App Store or Google Play Store.
            </p>
          </div>
        </header>
        <div className="container sp-main">
          <div className="apps-grid">
            {apps.map(app => (
              <article key={app.id} className="app-card">
                {app.link ? (
                  <Link to={app.link} className="app-card-link">
                    <AppCardBody app={app} />
                    <span className="view-details">
                      {app.link.startsWith('/apps/') ? 'View Details →' : 'Play free online →'}
                    </span>
                  </Link>
                ) : (
                  <div className="app-card-link">
                    <AppCardBody app={app} />
                  </div>
                )}
                <div className="app-card-stores">
                  <StoreBadges app={app.storeKey} placement="apps-page" page="/apps" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Apps;
